import clsx from 'clsx'
import { kebabCase } from 'lodash'
import React from 'react'

import { getAriaLabel } from 'components/common/Button'
import theme from 'theme'
import { omit } from 'utils/obj'

function setColor(color) {
  return {
    color: theme.colors[color][0],

    '&:hover, &:focus': {
      color: theme.colors[color][1]
    },

    '&:disabled': {
      color: theme.colors[color][1]
    }
  }
}

const BUTTON_TYPES = {
  primary: {
    color: theme.colors.blue[0],

    '&:hover, &:focus': {
      color: theme.colors.blue[1]
    },

    '&:disabled': {
      color: theme.colors.blue[0]
    }
  },
  secondary: {
    color: theme.colors.steel[1],

    '&:hover, &:focus': {
      color: theme.colors.steel[2]
    },

    '&:disabled': {
      color: theme.colors.steel[2]
    }
  },
  dark: {
    color: theme.colors.steel[2],

    '&:hover, &:focus': {
      color: theme.colors.steel[2]
    },

    '&:disabled': {
      color: theme.colors.steel[2]
    }
  },
  white: {
    color: theme.colors.white[0],

    '&:hover, &:focus': {
      color: theme.colors.white[0]
    },

    '&:disabled': {
      color: theme.colors.white[0]
    }
  },
  creative: setColor('green'),
  destructive: setColor('red')
}

type TextButtonProps = {
  className?: string
  disableStyles?: boolean
  primary?: boolean
  secondary?: boolean
  dark?: boolean
  white?: boolean
  creative?: boolean
  destructive?: boolean
  label?: React.ReactNode
  title?: React.ReactNode
  children?: React.ReactNode
}

interface TextButtonPropsAs extends TextButtonProps {
  as?: React.ElementType
  // unless explicitly defined here, we are getting ts warnings on this prop being passed through to the component.
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void
}

const TextButton = React.forwardRef<HTMLButtonElement, TextButtonPropsAs>(
  (
    {
      as: Component = 'button',
      className,
      label,
      title,
      disableStyles,
      children,
      ...rest
    },
    ref: React.Ref<any>
  ) => {
    const ariaLabel = getAriaLabel(label || title || children)

    const analytics = {
      'data-track-on': rest['data-track-on'] ?? 'click',
      'data-track-category': rest['data-track-category'] ?? 'general',
      'data-track-action': rest['data-track-action'] ?? kebabCase(ariaLabel),
      'data-track-label': rest['data-track-label'] ?? 'button'
    }

    const getColor = () => {
      if (disableStyles) {
        return {}
      }

      const matchingColors = Object.keys(BUTTON_TYPES).filter((t) => rest[t])

      return matchingColors.length > 0
        ? matchingColors.map((t) => BUTTON_TYPES[t])
        : BUTTON_TYPES['primary']
    }

    return (
      <Component
        ref={ref}
        aria-label={ariaLabel}
        className={clsx('TextButton', className)}
        css={[
          {
            '&:not(:disabled)': {
              cursor: 'pointer'
            },

            display: 'block',
            textTransform: 'none',
            background: 'none',
            outline: 'none',
            border: 0,
            fontSize: theme.fontSizes[1],
            fontWeight: theme.fontWeights[0],

            '&:hover, &:focus': {
              background: 'none'
            },

            '&:disabled': {
              background: 'none',
              opacity: theme.opacity[0],
              cursor: 'not-allowed'
            }
          },

          getColor()
        ]}
        type={Component === 'button' ? 'button' : null}
        {...omit(rest, Object.keys(BUTTON_TYPES))}
        {...analytics}
      >
        {children}
      </Component>
    )
  }
)

TextButton.displayName = 'TextButton'

export default TextButton
